import { NOTIFICATION_SOUND_FILES, NotificationSoundKey } from "@core/constants/notificationSound"
import { getNotificationCustomSoundBlob, getTreatmentEndCustomSoundBlob } from "@core/utils/treatmentEndCustomSoundStorage"

export const USER_CONFIGURABLE_NOTIFICATION_SOUND_KEYS = ["chatNotification", "kioskReception", "generalReception"] as const
export type UserConfigurableNotificationSoundKey = (typeof USER_CONFIGURABLE_NOTIFICATION_SOUND_KEYS)[number]

export interface UserFeatureSettings {
  medicalCertificateDiagnosisDateDefaultsToToday: boolean
  dashboardPatientCardDepartmentColors: Record<string, string>
  deviceLockEnabled: boolean
  deviceLockDurationMinutes: number | null
  deviceLockUsesCustomDuration: boolean
  deviceLockPin: string | null
  notificationSoundVolumes: Record<UserConfigurableNotificationSoundKey, number>
  chatNotificationAlarmEnabled: boolean
  chatNotificationCustomSoundFileName: string | null
  chatNotificationCustomSoundUpdatedAt: number | null
  kioskReceptionAlarmEnabled: boolean
  kioskReceptionCustomSoundFileName: string | null
  kioskReceptionCustomSoundUpdatedAt: number | null
  generalReceptionAlarmEnabled: boolean
  generalReceptionCustomSoundFileName: string | null
  generalReceptionCustomSoundUpdatedAt: number | null
  cidIncomingCallPopupEnabled: boolean
  treatmentEndAlarmEnabled: boolean
  treatmentEndAlarmRetryCount: number
  treatmentEndAlarmRetryInterval: number
  treatmentEndCustomSoundEnabled: boolean
  treatmentEndCustomSoundFileName: string | null
  treatmentEndCustomSoundUpdatedAt: number | null
  waitingStatusBoardEnabled: boolean
  kioskPatientCallEnabled: boolean
  kioskPatientCallToClinicEnabled: boolean
  kioskPatientCallToTreatmentRoomEnabled: boolean
  kioskPatientCallClinicButtonLabel: string
  kioskPatientCallTreatmentRoomButtonLabel: string
  kioskPatientCallClinicMessage: string
  kioskPatientCallTreatmentRoomMessage: string
  kioskPatientCallClinicAlimtalkEnabled: boolean
  kioskPatientCallClinicAlimtalkTemplateUuid: string
  kioskPatientCallTreatmentRoomAlimtalkEnabled: boolean
  kioskPatientCallTreatmentRoomAlimtalkTemplateUuid: string
  kioskPatientCallDisplaySeconds: number
  waitingStatusBoardHiddenDoctorUuids: string[]
  waitingStatusBoardCallFontSize: number
  waitingStatusBoardCallTextColor: string
  waitingStatusBoardCallBackgroundColor: string
  voice_gender: "f" | "m"
}

type LegacyUserFeatureSettings = Partial<UserFeatureSettings> & {
  kioskPatientCallFemaleEnabled?: boolean
  kioskPatientCallMaleEnabled?: boolean
}

const USER_FEATURE_SETTINGS_KEY_PREFIX = "userFeatureSettings_"
const KIOSK_PATIENT_CALL_DEFAULT_ON_MIGRATION_KEY_PREFIX = "kioskPatientCallDefaultOnMigrated_"
const USER_SOUND_SETTINGS_CHANGED_EVENT = "user-sound-settings-changed"

interface AudioPlaybackHandle {
  stop: () => void
  done: Promise<void>
}
export const DEFAULT_KIOSK_PATIENT_CALL_CLINIC_MESSAGE = "진료실로 들어오세요"
export const DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_MESSAGE = "치료실로 들어오세요"
export const DEFAULT_WAITING_STATUS_BOARD_CALL_FONT_SIZE = 64
export const DEFAULT_WAITING_STATUS_BOARD_CALL_TEXT_COLOR = "#ffffff"
export const DEFAULT_WAITING_STATUS_BOARD_CALL_BACKGROUND_COLOR = "#0a233a"
export const DEFAULT_KIOSK_PATIENT_CALL_CLINIC_BUTTON_LABEL = "진료실호출"
export const DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_BUTTON_LABEL = "치료실호출"

export type KioskPatientCallType = "clinic" | "treatmentRoom"

const DEFAULT_USER_FEATURE_SETTINGS: UserFeatureSettings = {
  medicalCertificateDiagnosisDateDefaultsToToday: true,
  dashboardPatientCardDepartmentColors: {},
  deviceLockEnabled: false,
  deviceLockDurationMinutes: null,
  deviceLockUsesCustomDuration: false,
  deviceLockPin: null,
  notificationSoundVolumes: { chatNotification: 100, kioskReception: 100, generalReception: 100 },
  chatNotificationAlarmEnabled: true,
  chatNotificationCustomSoundFileName: null,
  chatNotificationCustomSoundUpdatedAt: null,
  kioskReceptionAlarmEnabled: true,
  kioskReceptionCustomSoundFileName: null,
  kioskReceptionCustomSoundUpdatedAt: null,
  generalReceptionAlarmEnabled: true,
  generalReceptionCustomSoundFileName: null,
  generalReceptionCustomSoundUpdatedAt: null,
  cidIncomingCallPopupEnabled: true,
  treatmentEndAlarmEnabled: true,
  treatmentEndAlarmRetryCount: 1,
  treatmentEndAlarmRetryInterval: 1,
  treatmentEndCustomSoundEnabled: false,
  treatmentEndCustomSoundFileName: null,
  treatmentEndCustomSoundUpdatedAt: null,
  waitingStatusBoardEnabled: true,
  kioskPatientCallEnabled: true,
  kioskPatientCallToClinicEnabled: true,
  kioskPatientCallToTreatmentRoomEnabled: true,
  kioskPatientCallClinicButtonLabel: DEFAULT_KIOSK_PATIENT_CALL_CLINIC_BUTTON_LABEL,
  kioskPatientCallTreatmentRoomButtonLabel: DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_BUTTON_LABEL,
  kioskPatientCallClinicMessage: DEFAULT_KIOSK_PATIENT_CALL_CLINIC_MESSAGE,
  kioskPatientCallTreatmentRoomMessage: DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_MESSAGE,
  kioskPatientCallClinicAlimtalkEnabled: false,
  kioskPatientCallClinicAlimtalkTemplateUuid: "",
  kioskPatientCallTreatmentRoomAlimtalkEnabled: false,
  kioskPatientCallTreatmentRoomAlimtalkTemplateUuid: "",
  kioskPatientCallDisplaySeconds: 30,
  waitingStatusBoardHiddenDoctorUuids: [],
  waitingStatusBoardCallFontSize: DEFAULT_WAITING_STATUS_BOARD_CALL_FONT_SIZE,
  waitingStatusBoardCallTextColor: DEFAULT_WAITING_STATUS_BOARD_CALL_TEXT_COLOR,
  waitingStatusBoardCallBackgroundColor: DEFAULT_WAITING_STATUS_BOARD_CALL_BACKGROUND_COLOR,
  voice_gender: "f",
}

const normalizeKioskPatientCallMessage = (message: string | undefined, fallback: string): string => {
  const normalizedMessage = typeof message === "string" ? message.trim() : ""
  return normalizedMessage || fallback
}

const isHexColorValue = (value: string): boolean => /^#[0-9a-fA-F]{6}$/.test(value)

const normalizeDashboardPatientCardDepartmentColors = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {}
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>((acc, [departmentUuid, color]) => {
    const normalizedDepartmentUuid = departmentUuid.trim()
    const normalizedColor = typeof color === "string" ? color.trim() : ""

    if (!normalizedDepartmentUuid || !isHexColorValue(normalizedColor)) {
      return acc
    }

    acc[normalizedDepartmentUuid] = normalizedColor
    return acc
  }, {})
}

const normalizeNotificationSoundVolume = (volume: unknown): number =>
  typeof volume === "number" && Number.isFinite(volume) ? Math.max(0, Math.min(100, volume)) : 100

export const getNotificationSoundVolume = (settings: UserFeatureSettings, soundKey: UserConfigurableNotificationSoundKey): number =>
  normalizeNotificationSoundVolume(settings.notificationSoundVolumes?.[soundKey])

const normalizeUserFeatureSettings = (settings: UserFeatureSettings): UserFeatureSettings => {
  const normalizedDeviceLockDurationMinutes =
    typeof settings.deviceLockDurationMinutes === "number" && Number.isFinite(settings.deviceLockDurationMinutes)
      ? Math.max(1, Math.floor(settings.deviceLockDurationMinutes))
      : null
  const normalizedDeviceLockPin = typeof settings.deviceLockPin === "string" && /^\d{4}$/.test(settings.deviceLockPin) ? settings.deviceLockPin : null

  return {
    ...settings,
    notificationSoundVolumes: {
      chatNotification: getNotificationSoundVolume(settings, "chatNotification"),
      kioskReception: getNotificationSoundVolume(settings, "kioskReception"),
      generalReception: getNotificationSoundVolume(settings, "generalReception"),
    },
    dashboardPatientCardDepartmentColors: normalizeDashboardPatientCardDepartmentColors(settings.dashboardPatientCardDepartmentColors),
    deviceLockEnabled: Boolean(settings.deviceLockEnabled) && normalizedDeviceLockDurationMinutes !== null,
    deviceLockDurationMinutes: normalizedDeviceLockDurationMinutes,
    deviceLockUsesCustomDuration: Boolean(settings.deviceLockUsesCustomDuration),
    deviceLockPin: normalizedDeviceLockPin,
    kioskPatientCallClinicButtonLabel: normalizeKioskPatientCallMessage(
      settings.kioskPatientCallClinicButtonLabel,
      DEFAULT_KIOSK_PATIENT_CALL_CLINIC_BUTTON_LABEL
    ),
    kioskPatientCallTreatmentRoomButtonLabel: normalizeKioskPatientCallMessage(
      settings.kioskPatientCallTreatmentRoomButtonLabel,
      DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_BUTTON_LABEL
    ),
    kioskPatientCallClinicMessage: normalizeKioskPatientCallMessage(
      settings.kioskPatientCallClinicMessage,
      DEFAULT_KIOSK_PATIENT_CALL_CLINIC_MESSAGE
    ),
    kioskPatientCallTreatmentRoomMessage: normalizeKioskPatientCallMessage(
      settings.kioskPatientCallTreatmentRoomMessage,
      DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_MESSAGE
    ),
    kioskPatientCallClinicAlimtalkEnabled: settings.kioskPatientCallClinicAlimtalkEnabled === true,
    kioskPatientCallClinicAlimtalkTemplateUuid:
      typeof settings.kioskPatientCallClinicAlimtalkTemplateUuid === "string" ? settings.kioskPatientCallClinicAlimtalkTemplateUuid : "",
    kioskPatientCallTreatmentRoomAlimtalkEnabled: settings.kioskPatientCallTreatmentRoomAlimtalkEnabled === true,
    kioskPatientCallTreatmentRoomAlimtalkTemplateUuid:
      typeof settings.kioskPatientCallTreatmentRoomAlimtalkTemplateUuid === "string"
        ? settings.kioskPatientCallTreatmentRoomAlimtalkTemplateUuid
        : "",
    kioskPatientCallDisplaySeconds:
      Number.isFinite(Number(settings.kioskPatientCallDisplaySeconds)) && Number(settings.kioskPatientCallDisplaySeconds) > 0
        ? Math.floor(Number(settings.kioskPatientCallDisplaySeconds))
        : DEFAULT_USER_FEATURE_SETTINGS.kioskPatientCallDisplaySeconds,
    waitingStatusBoardHiddenDoctorUuids: Array.isArray(settings.waitingStatusBoardHiddenDoctorUuids)
      ? [
          ...new Set(
            settings.waitingStatusBoardHiddenDoctorUuids
              .filter(uuid => typeof uuid === "string")
              .map(uuid => uuid.trim())
              .filter(Boolean)
          ),
        ]
      : [],
    waitingStatusBoardCallFontSize:
      Number.isFinite(Number(settings.waitingStatusBoardCallFontSize)) && Number(settings.waitingStatusBoardCallFontSize) > 0
        ? Math.floor(Number(settings.waitingStatusBoardCallFontSize))
        : DEFAULT_USER_FEATURE_SETTINGS.waitingStatusBoardCallFontSize,
    waitingStatusBoardCallTextColor: isHexColorValue(settings.waitingStatusBoardCallTextColor)
      ? settings.waitingStatusBoardCallTextColor
      : DEFAULT_USER_FEATURE_SETTINGS.waitingStatusBoardCallTextColor,
    waitingStatusBoardCallBackgroundColor: isHexColorValue(settings.waitingStatusBoardCallBackgroundColor)
      ? settings.waitingStatusBoardCallBackgroundColor
      : DEFAULT_USER_FEATURE_SETTINGS.waitingStatusBoardCallBackgroundColor,
  }
}

const getStorageKey = (memberUuid?: string | null): string => {
  return `${USER_FEATURE_SETTINGS_KEY_PREFIX}${memberUuid || "guest"}`
}

const migrateKioskPatientCallDefaultToOn = (settings: LegacyUserFeatureSettings, memberUuid?: string | null): LegacyUserFeatureSettings => {
  if (typeof window === "undefined") {
    return settings
  }

  const migrationKey = `${KIOSK_PATIENT_CALL_DEFAULT_ON_MIGRATION_KEY_PREFIX}${memberUuid || "guest"}`

  try {
    if (localStorage.getItem(migrationKey) === "true") {
      return settings
    }

    const migratedSettings = {
      ...settings,
      kioskPatientCallEnabled: true,
    }
    localStorage.setItem(getStorageKey(memberUuid), JSON.stringify(migratedSettings))
    localStorage.setItem(migrationKey, "true")
    return migratedSettings
  } catch (error) {
    console.error("환자호출 기본 설정 마이그레이션 실패:", error)
    return {
      ...settings,
      kioskPatientCallEnabled: true,
    }
  }
}

const readUserFeatureSettings = (memberUuid?: string | null): LegacyUserFeatureSettings => {
  if (typeof window === "undefined") {
    return {}
  }

  try {
    const raw = localStorage.getItem(getStorageKey(memberUuid))
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw) as LegacyUserFeatureSettings
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch (error) {
    console.error("사용자 기능 설정 로드 실패:", error)
    return {}
  }
}

const writeUserFeatureSettings = (settings: UserFeatureSettings, memberUuid?: string | null): void => {
  if (typeof window === "undefined") {
    return
  }

  try {
    localStorage.setItem(getStorageKey(memberUuid), JSON.stringify(settings))
  } catch (error) {
    console.error("사용자 기능 설정 저장 실패:", error)
  }
}

export const getKioskReceptionAlarmEnabled = (memberUuid?: string | null): boolean => {
  const settings = getUserFeatureSettings(memberUuid)
  return settings.kioskReceptionAlarmEnabled
}

export const setKioskReceptionAlarmEnabled = (enabled: boolean, memberUuid?: string | null): void => {
  const current = getUserFeatureSettings(memberUuid)
  setUserFeatureSettings(
    {
      ...current,
      kioskReceptionAlarmEnabled: enabled,
    },
    memberUuid
  )
}

export const getUserFeatureSettings = (memberUuid?: string | null): UserFeatureSettings => {
  const current = migrateKioskPatientCallDefaultToOn(readUserFeatureSettings(memberUuid), memberUuid)
  const migratedVoiceGender = current.voice_gender || (current.kioskPatientCallMaleEnabled ? "m" : "f")

  return normalizeUserFeatureSettings({
    ...DEFAULT_USER_FEATURE_SETTINGS,
    ...current,
    voice_gender: migratedVoiceGender,
  })
}

export const setUserFeatureSettings = (settings: UserFeatureSettings, memberUuid?: string | null): void => {
  const normalizedSettings = normalizeUserFeatureSettings(settings)
  writeUserFeatureSettings(normalizedSettings, memberUuid)
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(USER_SOUND_SETTINGS_CHANGED_EVENT, {
        detail: {
          memberUuid: memberUuid || "guest",
          settings: normalizedSettings,
        },
      })
    )
  }
}

export const getKioskPatientCallMessage = (settings: UserFeatureSettings, callType: KioskPatientCallType): string => {
  if (callType === "clinic") {
    return normalizeKioskPatientCallMessage(settings.kioskPatientCallClinicMessage, DEFAULT_KIOSK_PATIENT_CALL_CLINIC_MESSAGE)
  }

  return normalizeKioskPatientCallMessage(settings.kioskPatientCallTreatmentRoomMessage, DEFAULT_KIOSK_PATIENT_CALL_TREATMENT_ROOM_MESSAGE)
}

export const USER_SOUND_SETTINGS_CHANGED_EVENT_NAME = USER_SOUND_SETTINGS_CHANGED_EVENT
export const USER_FEATURE_SETTINGS_CHANGED_EVENT_NAME = USER_SOUND_SETTINGS_CHANGED_EVENT

export const getNotificationCustomSoundFileName = (settings: UserFeatureSettings, soundKey: UserConfigurableNotificationSoundKey): string | null => {
  switch (soundKey) {
    case "chatNotification":
      return settings.chatNotificationCustomSoundFileName
    case "kioskReception":
      return settings.kioskReceptionCustomSoundFileName
    case "generalReception":
      return settings.generalReceptionCustomSoundFileName
  }
}

export const applyNotificationCustomSoundMetadata = (
  settings: UserFeatureSettings,
  soundKey: UserConfigurableNotificationSoundKey,
  fileName: string | null,
  updatedAt: number | null
): UserFeatureSettings => {
  switch (soundKey) {
    case "chatNotification":
      return {
        ...settings,
        chatNotificationCustomSoundFileName: fileName,
        chatNotificationCustomSoundUpdatedAt: updatedAt,
      }
    case "kioskReception":
      return {
        ...settings,
        kioskReceptionCustomSoundFileName: fileName,
        kioskReceptionCustomSoundUpdatedAt: updatedAt,
      }
    case "generalReception":
      return {
        ...settings,
        generalReceptionCustomSoundFileName: fileName,
        generalReceptionCustomSoundUpdatedAt: updatedAt,
      }
  }
}

export const clearNotificationCustomSoundMetadata = (
  settings: UserFeatureSettings,
  soundKey: UserConfigurableNotificationSoundKey
): UserFeatureSettings => {
  return applyNotificationCustomSoundMetadata(settings, soundKey, null, null)
}

const playAudioFile = (src: string, volume = 100) => {
  if (typeof window === "undefined") {
    return
  }

  const audio = new Audio(src)
  audio.volume = normalizeNotificationSoundVolume(volume) / 100
  audio.currentTime = 0
  void audio.play().catch(error => {
    console.error("알림음 재생 실패:", error)
  })
}

const createAudioPlaybackHandle = (audio: HTMLAudioElement, cleanup?: () => void): AudioPlaybackHandle => {
  let finished = false
  let resolveDone: (() => void) | null = null

  const done = new Promise<void>(resolve => {
    resolveDone = resolve
  })

  const finish = () => {
    if (finished) return
    finished = true
    cleanup?.()
    resolveDone?.()
  }

  audio.addEventListener("ended", finish, { once: true })
  audio.addEventListener("error", finish, { once: true })

  audio.currentTime = 0
  void audio.play().catch(error => {
    console.error("알림음 재생 실패:", error)
    finish()
  })

  return {
    stop: () => {
      try {
        audio.pause()
        audio.currentTime = 0
      } catch {
        // noop
      }
      finish()
    },
    done,
  }
}

const playAudioFileWithHandle = (src: string): AudioPlaybackHandle | null => {
  if (typeof window === "undefined") {
    return null
  }

  const audio = new Audio(src)
  return createAudioPlaybackHandle(audio)
}

const playAudioBlob = async (blob: Blob, volume = 100): Promise<boolean> => {
  if (typeof window === "undefined") {
    return false
  }

  const objectUrl = URL.createObjectURL(blob)
  const audio = new Audio(objectUrl)
  audio.volume = normalizeNotificationSoundVolume(volume) / 100
  audio.currentTime = 0

  const cleanup = () => {
    URL.revokeObjectURL(objectUrl)
  }

  audio.addEventListener("ended", cleanup, { once: true })
  audio.addEventListener("error", cleanup, { once: true })

  try {
    await audio.play()
    return true
  } catch (error) {
    cleanup()
    console.error("사용자 알림음 재생 실패:", error)
    return false
  }
}

const playAudioBlobWithHandle = (blob: Blob): AudioPlaybackHandle | null => {
  if (typeof window === "undefined") {
    return null
  }

  const objectUrl = URL.createObjectURL(blob)
  const audio = new Audio(objectUrl)
  return createAudioPlaybackHandle(audio, () => {
    URL.revokeObjectURL(objectUrl)
  })
}

export const playNotificationSound = (soundKey: NotificationSoundKey, volume = 100) => {
  try {
    playAudioFile(NOTIFICATION_SOUND_FILES[soundKey], volume)
  } catch (error) {
    console.error("알림음 재생 실패:", error)
  }
}

export const playStoredNotificationCustomSound = async (
  soundKey: UserConfigurableNotificationSoundKey,
  memberUuid?: string | null,
  volume = getNotificationSoundVolume(getUserFeatureSettings(memberUuid), soundKey)
): Promise<boolean> => {
  try {
    const blob = await getNotificationCustomSoundBlob(soundKey, memberUuid)
    if (!blob) return false
    return playAudioBlob(blob, volume)
  } catch (error) {
    console.error("커스텀 알림음 재생 실패:", error)
    return false
  }
}

const playNotificationSoundWithCustom = async (soundKey: UserConfigurableNotificationSoundKey, memberUuid?: string | null) => {
  const settings = getUserFeatureSettings(memberUuid)
  const volume = getNotificationSoundVolume(settings, soundKey)
  const customSoundFileName = getNotificationCustomSoundFileName(settings, soundKey)

  if (customSoundFileName) {
    const played = await playStoredNotificationCustomSound(soundKey, memberUuid, volume)
    if (played) {
      return
    }
  }

  playNotificationSound(soundKey, volume)
}

export const playKioskReceptionAlarm = (memberUuid?: string | null) => {
  if (typeof window === "undefined") {
    return
  }

  void playNotificationSoundWithCustom("kioskReception", memberUuid).catch(error => {
    console.error("키오스크 접수 알림음 재생 실패:", error)
  })
}

export const playChatNotificationAlarm = (memberUuid?: string | null) => {
  void playNotificationSoundWithCustom("chatNotification", memberUuid).catch(error => {
    console.error("채팅 알림음 재생 실패:", error)
  })
}

export const playGeneralReceptionAlarm = (memberUuid?: string | null) => {
  void playNotificationSoundWithCustom("generalReception", memberUuid).catch(error => {
    console.error("일반 접수 알림음 재생 실패:", error)
  })
}

export const playStoredTreatmentEndCustomSound = async (memberUuid?: string | null): Promise<boolean> => {
  try {
    const blob = await getTreatmentEndCustomSoundBlob(memberUuid)
    if (!blob) return false
    return playAudioBlob(blob)
  } catch (error) {
    console.error("커스텀 치료 종료 알림음 재생 실패:", error)
    return false
  }
}

const playTreatmentEndAlarmOnce = async (memberUuid?: string | null) => {
  try {
    const settings = getUserFeatureSettings(memberUuid)
    if (settings.treatmentEndCustomSoundEnabled) {
      const blob = await getTreatmentEndCustomSoundBlob(memberUuid)
      if (blob) {
        const customHandle = playAudioBlobWithHandle(blob)
        if (customHandle) {
          return customHandle
        }
      }
    }
    return playAudioFileWithHandle(NOTIFICATION_SOUND_FILES.treatmentEnd)
  } catch (error) {
    console.error("치료 종료 알림음 재생 실패:", error)
    return playAudioFileWithHandle(NOTIFICATION_SOUND_FILES.treatmentEnd)
  }
}

export interface TreatmentEndAlarmHandle {
  cancel: () => void
}

// 전역 활성 알람 핸들 맵 (key = alertKey)
const activeAlarmHandles = new Map<string, TreatmentEndAlarmHandle>()

export const cancelTreatmentEndAlarm = (alertKey: string) => {
  const handle = activeAlarmHandles.get(alertKey)
  if (handle) {
    handle.cancel()
    activeAlarmHandles.delete(alertKey)
  }
}

export const cancelAllTreatmentEndAlarms = () => {
  activeAlarmHandles.forEach(handle => handle.cancel())
  activeAlarmHandles.clear()
}

/**
 * 치료 종료 알림음을 재시도 설정에 따라 반복 재생한다.
 * 각 재생은 이전 재생이 완전히 끝난 뒤 retryInterval 초를 기다리고 시작한다.
 * 반환된 handle.cancel()을 호출하면 남은 반복을 중단한다.
 * onComplete 콜백은 모든 반복이 끝나면(취소 포함) 호출된다.
 */
export const playTreatmentEndAlarm = (memberUuid?: string | null, alertKey?: string, onComplete?: () => void): TreatmentEndAlarmHandle => {
  const settings = getUserFeatureSettings(memberUuid)
  const retryCount = Math.max(1, Math.min(10, settings.treatmentEndAlarmRetryCount || 1))
  const retryInterval = Math.max(1, Math.min(10, settings.treatmentEndAlarmRetryInterval || 1))

  let cancelled = false
  let completed = false
  let timerId: ReturnType<typeof setTimeout> | undefined
  let playIndex = 0
  let currentPlayback: AudioPlaybackHandle | null = null

  const finish = () => {
    if (completed) return
    completed = true
    if (alertKey) activeAlarmHandles.delete(alertKey)
    onComplete?.()
  }

  const playNext = async () => {
    if (cancelled || playIndex >= retryCount) {
      finish()
      return
    }

    playIndex += 1

    currentPlayback = await playTreatmentEndAlarmOnce(memberUuid)

    if (cancelled) {
      currentPlayback?.stop()
      currentPlayback = null
      finish()
      return
    }

    if (playIndex >= retryCount) {
      if (!currentPlayback) {
        finish()
        return
      }

      void currentPlayback.done.then(() => {
        currentPlayback = null
        finish()
      })
      return
    }

    if (!currentPlayback) {
      timerId = setTimeout(() => {
        timerId = undefined
        void playNext()
      }, retryInterval * 1000)
      return
    }

    void currentPlayback.done.then(() => {
      currentPlayback = null

      if (cancelled) {
        finish()
        return
      }

      timerId = setTimeout(() => {
        timerId = undefined
        void playNext()
      }, retryInterval * 1000)
    })
  }

  const handle: TreatmentEndAlarmHandle = {
    cancel: () => {
      cancelled = true
      if (timerId !== undefined) clearTimeout(timerId)
      currentPlayback?.stop()
      currentPlayback = null
      finish()
    },
  }

  if (alertKey) {
    // 기존 알람이 있으면 취소
    cancelTreatmentEndAlarm(alertKey)
    activeAlarmHandles.set(alertKey, handle)
  }

  void playNext()
  return handle
}
